import { getSupabase } from "@/lib/supabase/client";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

const AdminRoadmapFull = lazy(() => import("./AdminRoadmapFull"));

export default function AdminRoadmapPage() {
  const sb = getSupabase();

  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    searchParams.get("rp")
  );
  const didInitPick = useRef(false);

  const sortProjects = useCallback((rows: any[]) => {
    return [...(rows ?? [])].sort((a, b) => {
      const ap = a?.is_published ? 1 : 0;
      const bp = b?.is_published ? 1 : 0;
      if (ap !== bp) return bp - ap;
      const av = Number.isFinite(a?.version) ? a.version : 0;
      const bv = Number.isFinite(b?.version) ? b.version : 0;
      if (av !== bv) return bv - av;
      const at = String(a?.title ?? "").toLowerCase();
      const bt = String(b?.title ?? "").toLowerCase();
      if (at !== bt) return at.localeCompare(bt);
      return String(a?.id ?? "").localeCompare(String(b?.id ?? ""));
    });
  }, []);

  const displayName = (p: any) => p?.title ?? "Untitled";

  useEffect(() => {
    (async () => {
      const { data, error } = await sb.from("roadmap_projects").select("*");
      if (error) {
        console.error(error);
        toast.error(error.message ?? "Failed to load roadmap projects");
        return;
      }
      setProjects(sortProjects(data ?? []));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // initial pick — run ONCE after projects arrive
  useEffect(() => {
    if (didInitPick.current) return;
    if (!projects.length) return;

    const urlId = searchParams.get("rp");
    const exists = urlId && projects.some((p) => String(p.id) === urlId);
    const published = projects.find((p) => p.is_published)?.id;
    const first = projects[0]?.id ?? null;

    const initial = (exists ? urlId : null) ?? published ?? first;
    setSelectedProjectId(initial);
    if (initial) {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set("rp", String(initial));
          return p;
        },
        { replace: true }
      );
    }
    didInitPick.current = true;
  }, [projects, searchParams, setSearchParams]);

  // user change -> stick to URL
  const onProjectChange = (id: string) => {
    setSelectedProjectId(id);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("rp", id);
        return p;
      },
      { replace: true }
    );
  };

  const [publishing, setPublishing] = useState(false);
  async function handlePublish() {
    if (!selectedProjectId) return;
    try {
      setPublishing(true);
      const { error } = await sb.rpc("publish_roadmap", {
        p_project_id: selectedProjectId,
      });
      if (error) throw error;
      toast.success("Published! This roadmap is now live.");

      const { data, error: e2 } = await sb.from("roadmap_projects").select("*");
      if (e2) throw e2;
      setProjects(sortProjects(data ?? []));
    } catch (e: any) {
      toast.error(e?.message ?? "Publish failed");
    } finally {
      setPublishing(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-slate-700">Project:</label>
        <select
          className="rounded-md border px-2 py-1 min-w-[280px]"
          value={selectedProjectId ?? ""}
          onChange={(e) => onProjectChange(e.target.value)}
        >
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {displayName(p)} · v{Number.isFinite(p?.version) ? p.version : 0}
              {p.is_published ? " (Published)" : ""}
            </option>
          ))}
        </select>

        <button
          className="ml-auto rounded-lg bg-emerald-600 text-white px-3 py-2 disabled:opacity-60"
          disabled={!selectedProjectId || publishing}
          onClick={handlePublish}
          title="Make this roadmap the public blueprint"
        >
          {publishing ? "Publishing…" : "Publish"}
        </button>

        {projects.find((p) => p.id === selectedProjectId)?.is_published && (
          <span className="ml-2 inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 text-xs px-2 py-0.5">
            Published
          </span>
        )}
      </div>

      <Suspense fallback={<div style={{ padding: 16 }}>Loading admin…</div>}>
        <AdminRoadmapFull
          hideHeader={true}
          projectId={selectedProjectId}
          onPublish={handlePublish}
          publishing={publishing}
        />
      </Suspense>
    </div>
  );
}
